/*
 * CSAT Tech Support survey — Preact app.
 * Renders into #csat-survey-root, reads settings from the adjacent
 * <script type="application/json" data-csat-config> emitted by
 * sections/csat-survey.liquid. Submits application/x-www-form-urlencoded
 * to config.endpoint — the FuelTech app proxy route (/apps/ft-app/csat),
 * signed by Shopify (HMAC) and validated server-side, which runs the
 * Admin API metaobjectCreate. Field names follow the app contract:
 * rep, rating_support, rating_timeliness, comment, email, ticket,
 * source, website (honeypot). The storefront never calls Shopify's
 * Admin API directly and never holds an Admin token.
 * Depends on the global htmPreact (assets/htm-preact-standalone.js).
 */
(function () {
  var HP = window.htmPreact;
  if (!HP) return;
  var html = HP.html;
  var render = HP.render;
  var useState = HP.useState;
  var useEffect = HP.useEffect;

  var root = document.getElementById('csat-survey-root');
  if (!root) return;

  var configEl = document.querySelector('[data-csat-config]');
  var config = {};
  try {
    config = JSON.parse(configEl ? configEl.textContent : '{}') || {};
  } catch (e) {
    config = {};
  }

  var STAFF = Array.isArray(config.staff) ? config.staff : [];
  var MAX = parseInt(config.commentsMax, 10) || 500;

  // Origem de rep e ticket, em ordem: window.ftCsatLink (posto por
  // snippets/csat-link-params.liquid no topo do head, que ja limpou a URL),
  // sessionStorage (recarga da pagina, inclusive a do consent-revoke-reload)
  // e, por ultimo, a propria URL (fragmento, depois query string), para o caso
  // de a section ser usada num template sem o snippet do head. Nesse ultimo
  // caso a limpeza e tardia: os pixels ja capturaram a URL. O fragmento e o
  // formato principal do link: #rep=<nome>&ticket=<id>.
  var LINK_KEY = 'ft-csat-link';
  var LINK_FIELDS = ['rep', 'ticket'];

  function readLinkFromUrl() {
    var found = {};
    var inHash = false;
    function read(text, isHash) {
      if (!text || text.length < 2) return;
      var p = new URLSearchParams(text.slice(1));
      for (var i = 0; i < LINK_FIELDS.length; i++) {
        if (!p.has(LINK_FIELDS[i])) continue;
        if (isHash) inHash = true;
        var v = (p.get(LINK_FIELDS[i]) || '').trim();
        if (v && !found[LINK_FIELDS[i]]) found[LINK_FIELDS[i]] = v.slice(0, 100);
      }
    }
    read(window.location.hash, true);
    read(window.location.search, false);
    if (found.rep || found.ticket) {
      try {
        var q = new URLSearchParams(window.location.search);
        for (var j = 0; j < LINK_FIELDS.length; j++) q.delete(LINK_FIELDS[j]);
        var search = q.toString();
        var clean = window.location.pathname + (search ? '?' + search : '') + (inHash ? '' : window.location.hash);
        window.history.replaceState(window.history.state, '', clean);
      } catch (e) { /* sem history */ }
    }
    return found;
  }

  function readLink() {
    var g = window.ftCsatLink;
    if (g && (g.rep || g.ticket)) {
      try { delete window.ftCsatLink; } catch (e) { /* propriedade nao configuravel */ }
      return g;
    }
    try {
      var raw = window.sessionStorage.getItem(LINK_KEY);
      if (raw) {
        var s = JSON.parse(raw);
        if (s && (s.rep || s.ticket)) return s;
      }
    } catch (e) { /* sem storage */ }
    var u = readLinkFromUrl();
    if (u.rep || u.ticket) {
      try { window.sessionStorage.setItem(LINK_KEY, JSON.stringify(u)); } catch (e) { /* sem storage */ }
    }
    return u;
  }
  function clearLink() {
    try { window.sessionStorage.removeItem(LINK_KEY); } catch (e) { /* sem storage */ }
  }

  var link = readLink();
  var repParam = (link.rep || '').trim();
  var ticketParam = (link.ticket || '').trim();

  // localStorage key: scoped to the link identity so a new support
  // interaction (new link) is never blocked by a previous submission.
  var storeKey =
    'ftCsatSubmitted:v1:' +
    (repParam ? repParam.toLowerCase() : 'general') +
    (ticketParam ? ':' + ticketParam.toLowerCase() : '');

  function readStore() {
    try {
      var raw = window.localStorage.getItem(storeKey);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }
  function writeStore(data) {
    try {
      window.localStorage.setItem(storeKey, JSON.stringify(data));
    } catch (e) {
      /* private mode / disabled storage — non-fatal */
    }
  }
  function clearStore() {
    try {
      window.localStorage.removeItem(storeKey);
    } catch (e) {
      /* non-fatal */
    }
  }

  function matchStaff(value) {
    if (!value) return '';
    var v = value.toLowerCase();
    for (var i = 0; i < STAFF.length; i++) {
      if (STAFF[i].toLowerCase() === v) return STAFF[i];
    }
    return '';
  }

  var STAR_FILLED =
    'M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z';

  function Star(props) {
    return props.filled
      ? html`<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d=${STAR_FILLED} /></svg>`
      : html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" aria-hidden="true"><path d=${STAR_FILLED} /></svg>`;
  }

  function StarRating(props) {
    var value = props.value;
    var onChange = props.onChange;
    var hoverState = useState(0);
    var hover = hoverState[0];
    var setHover = hoverState[1];
    var stars = [1, 2, 3, 4, 5];

    function onKey(e) {
      if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
        e.preventDefault();
        onChange(Math.min(5, (value || 0) + 1));
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
        e.preventDefault();
        onChange(Math.max(1, (value || 0) - 1));
      }
    }

    return html`
      <div
        class="csat-stars"
        role="radiogroup"
        aria-label=${props.ariaLabel}
        onMouseLeave=${function () {
          setHover(0);
        }}
      >
        ${stars.map(function (n) {
          var active = (hover || value) >= n;
          return html`<button
            type="button"
            class=${'csat-star-btn' + (active ? ' is-on' : '')}
            role="radio"
            aria-checked=${value === n}
            aria-label=${n + ' of 5'}
            onMouseEnter=${function () {
              setHover(n);
            }}
            onFocus=${function () {
              setHover(n);
            }}
            onBlur=${function () {
              setHover(0);
            }}
            onKeyDown=${onKey}
            onClick=${function () {
              onChange(n);
            }}
          >
            <${Star} filled=${active} />
          </button>`;
        })}
      </div>
    `;
  }

  function ThankYou(props) {
    var rating = props.rating || 0;
    var stars = '';
    for (var i = 0; i < rating; i++) stars += '★';
    return html`
      <div class="csat-card csat-thanks">
        ${props.logo &&
        html`<img class="csat-logo" src=${props.logo} alt=${props.logoAlt} />`}
        <div class="csat-check" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20 6L9 17l-5-5" />
          </svg>
        </div>
        <h2 class="csat-thanks-title">${props.thankyouTitle}</h2>
        ${rating > 0 &&
        html`<p class="csat-thanks-rating">
          ${props.thankyouRatingLabel}
          <span class="csat-inline-stars" aria-label=${rating + ' of 5'}>${stars}</span>
        </p>`}
        ${props.thankyouNote && html`<p class="csat-footer">${props.thankyouNote}</p>`}
        <button type="button" class="csat-reset" onClick=${props.onReset}>
          ${props.resetLabel}
        </button>
      </div>
    `;
  }

  function App() {
    var prior = readStore();
    var staffState = useState(prior ? prior.staff || '' : matchStaff(repParam));
    var supportState = useState(prior ? prior.support || 0 : 0);
    var timeState = useState(prior ? prior.timeliness || 0 : 0);
    var commentsState = useState('');
    var emailState = useState('');
    var hpState = useState('');
    var statusState = useState(prior ? 'done' : 'idle');
    var errorState = useState('');

    var staff = staffState[0];
    var support = supportState[0];
    var timeliness = timeState[0];
    var comments = commentsState[0];
    var email = emailState[0];
    var hp = hpState[0];
    var status = statusState[0];
    var error = errorState[0];

    var doneRating = prior ? prior.support || 0 : support;

    var valid = staff !== '' && support > 0 && timeliness > 0;

    function reset() {
      clearStore();
      staffState[1](matchStaff(repParam));
      supportState[1](0);
      timeState[1](0);
      commentsState[1]('');
      emailState[1]('');
      statusState[1]('idle');
      errorState[1]('');
    }

    function submit(e) {
      e.preventDefault();
      if (!valid || status === 'submitting') return;
      // Honeypot: a filled hidden field means a bot — silently succeed.
      if (hp) {
        statusState[1]('done');
        return;
      }
      statusState[1]('submitting');
      errorState[1]('');

      // Test mode: skip the endpoint entirely and mock a successful submit.
      if (config.testMode) {
        setTimeout(function () {
          writeStore({ staff: staff, support: support, timeliness: timeliness });
          clearLink();
          statusState[1]('done');
        }, 500);
        return;
      }

      // App proxy contract (urlencoded POST): field names are validated by
      // the FuelTech app endpoint. URLSearchParams sets the
      // application/x-www-form-urlencoded Content-Type by itself.
      var payload = new URLSearchParams({
        rep: staff,
        rating_support: String(support),
        rating_timeliness: String(timeliness),
        comment: comments,
        email: email,
        ticket: ticketParam || '',
        source: repParam || '',
        website: hp
      });

      fetch(config.endpoint, {
        method: 'POST',
        body: payload
      })
        .then(function (res) {
          if (!res.ok) throw new Error('bad status ' + res.status);
          return res.json().catch(function () {
            return { ok: true };
          });
        })
        .then(function (data) {
          if (data && data.ok === false) throw new Error(data.error || 'rejected');
          writeStore({ staff: staff, support: support, timeliness: timeliness });
          clearLink();
          statusState[1]('done');
        })
        .catch(function () {
          statusState[1]('error');
          errorState[1](config.errorText || 'Something went wrong. Please try again.');
        });
    }

    if (status === 'done') {
      return html`<${ThankYou}
        rating=${doneRating}
        logo=${config.logo}
        logoAlt=${config.logoAlt}
        thankyouTitle=${config.thankyouTitle}
        thankyouRatingLabel=${config.thankyouRatingLabel}
        thankyouNote=${config.thankyouNote}
        resetLabel=${config.resetLabel}
        onReset=${reset}
      />`;
    }

    var subtitle = config.subtitle || '';
    if (repParam && staff && subtitle.indexOf('{rep}') !== -1) {
      subtitle = subtitle.replace('{rep}', staff);
    } else if (subtitle.indexOf('{rep}') !== -1) {
      subtitle = subtitle.replace('{rep}', '').trim();
    }

    return html`
      <form class="csat-card csat-form" onSubmit=${submit} novalidate>
        ${config.logo &&
        html`<img class="csat-logo" src=${config.logo} alt=${config.logoAlt} />`}
        <h2 class="csat-title">${config.title}</h2>
        ${subtitle && html`<p class="csat-subtitle">${subtitle}</p>`}

        <div class="csat-field">
          <label class="csat-label" for="csat-staff">
            ${config.staffLabel}<span class="csat-required">*</span>
          </label>
          <select
            id="csat-staff"
            class="csat-select"
            value=${staff}
            onChange=${function (e) {
              staffState[1](e.target.value);
            }}
          >
            <option value="">${config.staffPlaceholder}</option>
            ${STAFF.map(function (name) {
              return html`<option value=${name}>${name}</option>`;
            })}
          </select>
        </div>

        <div class="csat-field">
          <label class="csat-label">
            ${config.supportLabel}<span class="csat-required">*</span>
          </label>
          <${StarRating}
            value=${support}
            ariaLabel=${config.supportLabel}
            onChange=${function (n) {
              supportState[1](n);
            }}
          />
          <span class="csat-help">${config.scaleAnchors}</span>
        </div>

        <div class="csat-field">
          <label class="csat-label">
            ${config.timelinessLabel}<span class="csat-required">*</span>
          </label>
          <${StarRating}
            value=${timeliness}
            ariaLabel=${config.timelinessLabel}
            onChange=${function (n) {
              timeState[1](n);
            }}
          />
          <span class="csat-help">${config.scaleAnchors}</span>
        </div>

        <div class="csat-field">
          <label class="csat-label" for="csat-comments">${config.commentsLabel}</label>
          <textarea
            id="csat-comments"
            class="csat-textarea"
            maxlength=${MAX}
            placeholder=${config.commentsPlaceholder}
            value=${comments}
            onInput=${function (e) {
              commentsState[1](e.target.value);
            }}
          ></textarea>
          <span class="csat-counter">${comments.length}/${MAX}</span>
        </div>

        <div class="csat-field">
          <label class="csat-label" for="csat-email">${config.emailLabel}</label>
          <input
            id="csat-email"
            type="email"
            class="csat-input"
            placeholder=${config.emailPlaceholder}
            value=${email}
            onInput=${function (e) {
              emailState[1](e.target.value);
            }}
          />
        </div>

        <div class="csat-hp" aria-hidden="true">
          <label
            >Company
            <input
              type="text"
              tabindex="-1"
              autocomplete="off"
              value=${hp}
              onInput=${function (e) {
                hpState[1](e.target.value);
              }}
          /></label>
        </div>

        <button type="submit" class="csat-submit" disabled=${!valid || status === 'submitting'}>
          ${status === 'submitting' ? config.submitBusyLabel : config.submitLabel}
        </button>

        ${status === 'error' && html`<p class="csat-error">${error}</p>`}
        ${config.footerNote &&
        html`<p class="csat-footer">${config.footerNote}</p>`}
      </form>
    `;
  }

  render(html`<${App} />`, root);
})();
